import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent, useReducedMotion, easeInOut } from "framer-motion";
import { Eyebrow } from "@/components/Eyebrow";
import { cn } from "@/lib/utils";
import { PINNED_HEADER_TOP } from "@/lib/site";

const CHAPTERS = [
    {
        num: "01",
        label: "Diagnose.",
        heading: "First, we learn how you operate.",
        lines: [
            "Shadow your real workflows, not just your org chart.",
            "Map every tool and handoff.",
            "Then, we show you where AI can make the biggest impact.",
        ],
    },
    {
        num: "02",
        label: "Integrate.",
        heading: "Then we scaffold a system that fits.",
        lines: [
            "Parallel to tools you already use.",
            "Only where it earns its keep.",
            "And tested on real data.",
        ],
    },
    {
        num: "03",
        label: "Maintain.",
        heading: "Our team stays, and keeps the system sharp.",
        lines: [
            "Monitoring it live and fixing drift before you see it.",
            "On call for full system support.",
            "New AI tools vetted and recommended as they come to market.",
        ],
    },
];

function Panel({ chapter }) {
    return (
        <div className="flex-none w-1/3 h-screen flex items-center pt-36 pb-20">
            <div className="max-w-6xl mx-auto w-full px-8 grid grid-cols-12 gap-8 items-center">
                <div className="col-span-3">
                    <span className="font-display font-bold text-[9rem] leading-none text-foreground/10">
                        {chapter.num}
                    </span>
                </div>
                <div className="col-span-9 max-w-2xl">
                    <p className="eyebrow mb-4">{chapter.label}</p>
                    <h3 className="font-display font-semibold tracking-[-0.02em] text-4xl lg:text-5xl leading-[1.08]">
                        {chapter.heading.replace(".", "")}<span className="period">.</span>
                    </h3>
                    <ul className="mt-8 space-y-4">
                        {chapter.lines.map((l, i) => (
                            <li key={i} className="text-lg text-muted-foreground leading-relaxed border-l border-border pl-5">
                                {l}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

function DesktopHow() {
    const ref = useRef(null);
    const [active, setActive] = useState(0);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

    // dwell then glide: panels sit still while read, then move to the next
    const x = useTransform(
        scrollYProgress,
        [0, 0.18, 0.4, 0.58, 0.82, 1],
        ["0%", "0%", "-33.333%", "-33.333%", "-66.666%", "-66.666%"],
        { ease: easeInOut }
    );

    useMotionValueEvent(scrollYProgress, "change", (v) => {
        const idx = v < 0.29 ? 0 : v < 0.7 ? 1 : 2;
        setActive(idx);
    });

    return (
        <div ref={ref} className="hidden lg:block h-[340vh]">
            <div className="sticky top-0 h-screen overflow-hidden">
                <div className={`absolute ${PINNED_HEADER_TOP} left-1/2 -translate-x-1/2 z-10 max-w-6xl w-full px-8`}>
                    <Eyebrow>How we work</Eyebrow>
                    <h2 className="mt-4 font-display font-semibold tracking-[-0.02em] text-3xl">
                        Diagnose. Integrate. Maintain<span className="period">.</span>
                    </h2>
                </div>

                <motion.div style={{ x }} className="flex h-full w-[300%]">
                    {CHAPTERS.map((c) => (
                        <Panel key={c.num} chapter={c} />
                    ))}
                </motion.div>

                {/* progress dots */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2.5">
                    {CHAPTERS.map((_, i) => (
                        <span
                            key={i}
                            data-testid={`how-dot-${i}`}
                            className={cn(
                                "h-2 w-2 rounded-full transition-colors duration-300",
                                i === active ? "bg-foreground" : "bg-border"
                            )}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function StackedHow({ className }) {
    return (
        <div className={cn("max-w-6xl mx-auto px-5 sm:px-8", className)}>
            <Eyebrow>How we work</Eyebrow>
            <h2 className="mt-4 font-display font-semibold tracking-[-0.02em] text-4xl sm:text-5xl">
                Diagnose. Integrate. Maintain<span className="period">.</span>
            </h2>
            <div className="mt-12 space-y-14">
                {CHAPTERS.map((c) => (
                    <motion.div
                        key={c.num}
                        initial={{ opacity: 0, y: 28 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="font-display font-bold text-6xl text-foreground/10">{c.num}</span>
                        <p className="eyebrow mt-2 mb-3">{c.label}</p>
                        <h3 className="font-display font-semibold tracking-[-0.02em] text-2xl sm:text-3xl leading-[1.1]">
                            {c.heading.replace(".", "")}<span className="period">.</span>
                        </h3>
                        <ul className="mt-5 space-y-3">
                            {c.lines.map((l, i) => (
                                <li key={i} className="text-muted-foreground leading-relaxed border-l border-border pl-4">
                                    {l}
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                ))}
            </div>
        </div>
    );
}

export function HowItWorks() {
    const reduce = useReducedMotion();
    return (
        <section id="how-it-works" data-testid="how-it-works" className="py-20 sm:py-24 bg-background">
            {!reduce && <DesktopHow />}
            {reduce && <StackedHow className="hidden lg:block" />}
            <StackedHow className="lg:hidden" />
        </section>
    );
}
